<template>
    <base-dropdown v-if="(column.isEditing && canCopyValues) || (column.isEditing && isBudgetPage)">
        <template #trigger>
            <div class="flex justify-between">
                <default-button
                    v-if="!isBudgetPage"
                    :label="$t('storemanager.copy-values')"
                    color="emerald"
                    icon="clipboard-copy"
                    size="xxs"
                    @click.native="isValuesClicked = true; isFormulaClicked = false"
                />

                <default-button
                    class="ml-2"
                    :label="$t('formula.copy')"
                    icon="clipboard-copy"
                    size="xxs"
                    @click.native="isFormulaClicked = true; isValuesClicked = false"
                />
            </div>
        </template>

        <template #content v-if="isValuesClicked">
            <base-dropdown-link
                v-for="(entry, index) in copyableValues"
                :key="index"
                @click.native="copyValues(entry.column, column)"
            >
                {{ entry.label }}
            </base-dropdown-link>
        </template>

        <template #content v-else>
            <base-dropdown-link
                v-for="(entry, index) in copyableValues"
                :key="index"
                @click.native="copyFormulas(entry.column, column)"
            >
                {{ entry.label + ' - ' + $t('formula.copy') }}
            </base-dropdown-link>
        </template>
    </base-dropdown>
</template>

<script>
import DataColumn from '@/Components/Storemanager/Entities/DataColumn';
import Actual from '@/Models/Actual';
import ProfitAndLoss from '@/Models/ProfitAndLoss';
import Extrapolation from '@/Models/Extrapolation';
import Projection from '@/Models/Projection';
import BudgetEntry from '@/Models/BudgetEntry';
import DataColumnCollection from '@/Components/Storemanager/Entities/DataColumnCollection';

export default {
    props: {
        column: {
            type: DataColumn,
            required: true,
        },
        collection: {
            type: DataColumnCollection,
            required: true,
        },
        isBudgetPage: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            isValuesClicked: false,
            isFormulaClicked: false
        };
    },

    computed: {
        canCopyValues() {
            return this.copyableValues.length > 0;
        },
        copyableValues() {
            let columns = [];
            let typeChecked = false;
            let currentColumns = this.collection.columns;

            // Actual
            if (this.column.entity instanceof Actual) {
                typeChecked = true;
                let foundColumn = currentColumns.find(col => col.entity instanceof ProfitAndLoss);

                if (!!foundColumn && foundColumn.entity.id) {
                    columns.push({column: foundColumn, label: this.$t('general.profit_and_loss')});

                    columns.push({
                        id: foundColumn.entity.id,
                        label: this.$t('general.profit_and_loss'),
                        column: foundColumn,
                    });
                }
            }

            let foundExtrapolations = [];

            // ProfitAndLoss + after
            if (typeChecked || this.column.entity instanceof ProfitAndLoss) {
                typeChecked = true;

                foundExtrapolations = currentColumns.filter(
                    col => col.entity instanceof Extrapolation && col.entity.id,
                );
            }

            // Extrapolation + after
            if (this.column.entity instanceof Extrapolation) {
                typeChecked = true;

                foundExtrapolations = currentColumns.filter(
                    col => col.entity instanceof Extrapolation &&
                        moment.utc(col.entity.date).isBefore(this.column.entity.date) &&
                        col.entity.id,
                );
            }

            // Add found extrapolations
            if (typeChecked && foundExtrapolations.length > 0) {
                columns.push(...foundExtrapolations.reverse().map(col => ({
                    id: col.entity.id,
                    label: this.$t('general.extrapolation') + ' ' +
                        moment.utc(col.entity.date).format('DD. MMMM YYYY'),
                    column: col,
                })));
            }

            // Add projection
            if (typeChecked) {
                let foundColumn = currentColumns.find(col => col.entity instanceof Projection);

                if (!!foundColumn && foundColumn.entity.id) {
                    columns.push({
                        id: foundColumn.entity.id,
                        label: this.$t('general.projection'),
                        column: foundColumn,
                    });
                }
            }

            // Projection + after
            if (typeChecked || this.column.entity instanceof Projection) {
                let foundColumn = currentColumns.find(col => col.entity instanceof BudgetEntry);

                if (!!foundColumn && foundColumn.entity.id) {
                    columns.push({
                        id: foundColumn.entity.id,
                        label: this.$t('general.budget'),
                        column: foundColumn,
                    });
                }
            }

            // Budget Entry only
            if (this.isBudgetPage) {
                if (this.column.entity instanceof BudgetEntry) {
                    typeChecked = true;
                    let columnDate = moment(this.column.entity.date);
                    let targetDate = columnDate.subtract(1, 'months');
                    let foundColumn = currentColumns.find(col => col.entity instanceof BudgetEntry && moment(col.entity.date).isSame(targetDate, 'month'));

                    if (!foundColumn) {
                        foundColumn = currentColumns
                            .filter(col => col.entity instanceof BudgetEntry)
                            .reduce((latestCol, currentCol) => {
                                return !latestCol || moment(currentCol.entity.date).isAfter(moment(latestCol.entity.date)) ? currentCol : latestCol;
                            }, null);
                    }

                    if (!!foundColumn && foundColumn.entity.id) {
                        columns.push({
                            id: foundColumn.entity.id,
                            label: this.$t('general.budget') + ' - ' + moment.utc(foundColumn.entity.date).format('DD. MMMM YYYY'),
                            column: foundColumn,
                        });
                    }
                }
            }

            return columns;
        },
    },

    methods: {
        /**
         * @param {DataColumn} from
         * @param {DataColumn} to
         */
        copyValues(from, to) {
            to.entity.copyValues(from.entity);
        },

        /**
         * @param {DataColumn} from
         * @param {DataColumn} to
         */
        copyFormulas(from, to) {
            to.entity.copyFormulas(from.entity);
        }
    },
};
</script>
